import { request } from "./http";

// --- paging helpers ---
function normalizePagedResponse(res, { page = 0, size = 12 } = {}) {
    // Spring Page: { content, number, size, totalElements, totalPages }
    if (res && typeof res === "object" && Array.isArray(res.content)) {
        const p = Number.isFinite(Number(res.number)) ? Number(res.number) : page;
        const s = Number.isFinite(Number(res.size)) ? Number(res.size) : size;
        const totalElements = Number.isFinite(Number(res.totalElements))
            ? Number(res.totalElements)
            : Array.isArray(res.content)
            ? res.content.length
            : 0;
        const totalPages = Number.isFinite(Number(res.totalPages))
            ? Number(res.totalPages)
            : Math.max(1, Math.ceil(totalElements / (s || 1)));

        return {
            items: res.content,
            page: p,
            size: s,
            totalElements,
            totalPages,
            hasMore: p + 1 < totalPages,
        };
    }

    // Generic: { items: [] }
    if (res && typeof res === "object" && Array.isArray(res.items)) {
        const totalElements = res.items.length;
        const totalPages = Math.max(1, Math.ceil(totalElements / (size || 1)));
        const start = page * size;
        const end = start + size;
        return {
            items: res.items.slice(start, end),
            page,
            size,
            totalElements,
            totalPages,
            hasMore: end < totalElements,
        };
    }

    // Array fallback
    if (Array.isArray(res)) {
        const totalElements = res.length;
        const totalPages = Math.max(1, Math.ceil(totalElements / (size || 1)));
        const start = page * size;
        const end = start + size;
        return {
            items: res.slice(start, end),
            page,
            size,
            totalElements,
            totalPages,
            hasMore: end < totalElements,
        };
    }

    return { items: [], page, size, totalElements: 0, totalPages: 1, hasMore: false };
}

let _cacheAll = null;
let _cacheAt = 0;
const CACHE_TTL_MS = 30_000;

async function listAllCached() {
    const now = Date.now();
    if (_cacheAll && now - _cacheAt < CACHE_TTL_MS) return _cacheAll;
    const res = await request("/api/me/payments", { method: "GET" }).catch(() => []);
    const arr = Array.isArray(res) ? res : Array.isArray(res?.items) ? res.items : [];
    _cacheAll = arr;
    _cacheAt = now;
    return arr;
}

function invalidatePaymentsCache() {
    _cacheAll = null;
    _cacheAt = 0;
}

export const paymentsApi = {
    /**
     * Все транзакции текущего пользователя.
     * Ожидаемый формат ответа: массив PaymentDTO или { items: [...] }
     */
    listMy: () => request("/api/me/payments", { method: "GET" }),

    /**
     * Пагинированный список (готово к бэку с Pageable).
     * Сейчас: пробуем /api/me/payments/page, если его нет — пагинируем на фронте.
     */
    listMyPaged: async ({ page = 0, size = 12 } = {}) => {
        const qs = new URLSearchParams();
        qs.set("page", String(page));
        qs.set("size", String(size));

        // 1) server-side paging (future)
        try {
            const res = await request(`/api/me/payments/page?${qs.toString()}`, { method: "GET" });
            return normalizePagedResponse(res, { page, size });
        } catch {
            // ignore
        }

        // 2) client-side paging fallback
        const all = await listAllCached();
        // newest first if possible
        const sorted = [...all].sort((a, b) => {
            const da = new Date(a?.createdAt || a?.created_at || 0).getTime() || 0;
            const db = new Date(b?.createdAt || b?.created_at || 0).getTime() || 0;
            return db - da;
        });
        return normalizePagedResponse(sorted, { page, size });
    },

    /**
     * Инициировать возврат (если до сессии > 24ч)
     */
    refund: (paymentId) => {
        const id = String(paymentId ?? "").trim();
        if (!id) throw new Error("paymentId is required");
        invalidatePaymentsCache();
        return request(`/api/me/payments/${id}/refund`, { method: "POST" });
    },

    invalidateCache: invalidatePaymentsCache,
};
